import React, {useState, useEffect} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {ButtonLink} from '@homeaway/react-buttons';
import {LoadingOverlay} from '@homeaway/react-loading-overlay';
import FormSelect from '@homeaway/react-form-select';
import {FormTextArea, FormInput} from '@homeaway/react-form-components';
import axios from 'axios';
import PhoneInput from './PhoneInput';
import Checkbox from './Checkbox';

import './Form.less';

/* eslint-disable complexity */
const Form = ({
    brand,
    brandId,
    titleOwner,
    titleManager,
    subtitle,
    userType,
    countryCodes,
    languages,
    countryCode,
    legal,
    backText,
    submissionErrorMsg,
    onFormSubmitted,
    onBackClick,
    ...labels
}) => {
    const languagesArray = Object.values(languages);

    const pleaseSpecify = languagesArray.pop();

    const languageItems = languagesArray.map((language) => ({
        value: language,
        label: language,
    }));

    const {register, errors, handleSubmit, control, formState, setValue} =
        useForm({
            mode: 'onSubmit',
            reValidateMode: 'onChange',
            shouldFocusError: false,
            defaultValues: {
                brand,
                brandId,
                userType,
                comment: '',
                firstName: '',
                lastName: '',
                email: '',
                langPref: languagesArray[0],
                langPrefOther: '',
                LangDropdownLabel: '',
                phoneNumber: '',
                phoneCode: '',
                numProps: '',
                pmSoftware: '',
                optOut: false,
            },
        });

    const [otherPreferredLanguage, setOtherPreferredLanguage] = useState(false);
    const [submitError, setSubmitError] = useState(false);
    const [isOptOut, setOptOut] = useState(false);

    useEffect(() => {
        register({name: 'phoneNumber'}, {required: true});
        register({name: 'phoneCode'});
        register({name: 'countryCode'});
    }, [register]);

    const handlePreferredLanguageSelect = (e, onChange) => {
        const selectedValue = e.target.value;
        const otherLanguage = languagesArray[languagesArray.length - 1];

        setOtherPreferredLanguage(selectedValue === otherLanguage);

        if (selectedValue !== otherLanguage) {
            setValue('langPrefOther', '');
        }

        onChange(e);
    };

    const onSubmit = async (data) => {
        const salesforceData = {
            // generic fields
            Description: data.comment,
            FirstName: data.firstName,
            LastName: data.lastName,
            Phone: `+${data.phoneCode} ${data.phoneNumber}`,
            Email: data.email,
            Preferred_Language__c: data.langPrefOther || data.langPref,
            Company: `${data.firstName} ${data.lastName}`,
            RecordTypeID: '0120y0000007uKRAAY',
            LeadSource: 'LYP Contact Us',
            // FIXME service fails when this value is present (see https://expedia.slack.com/archives/C02AKRUTSUD/p1628591794014300)
            // Lead_Source_c__c: brand,
            BrandId__c: brandId,
            HasOptedOutOfEmail: isOptOut,
            Person_Type__c: userType === 'pm' ? 'Property Manager' : 'Owner',
            // pm specific fields
            Total_of_Properties_in_Inventory__c: data.numProps,
            Partner_Software_Web_Form__c: data.pmSoftware,
        };

        try {
            const response = await axios({
                method: 'post',
                // NOTE for local development start static-origin-server on port 8081
                // url: 'http://localhost:8081/l/submit-form/eg',
                url: `${document.location.origin}/l/submit-form/eg`,
                data: salesforceData,
            });

            if (response?.data?.submitted) {
                onFormSubmitted();
            }

            throw new Error();
        } catch (error) {
            setSubmitError(true);
        }
    };

    const getFormInputProps = (id, required = true) => ({
        as: FormInput,
        rules: {required},
        control,
        id,
        name: id,
        label: labels[id],
        errorMsg: errors[id] && 'Required',
    });

    return (
        <div className="form">
            <LoadingOverlay loading={formState.isSubmitting} />

            {userType === 'frbo' && (
                <h3 className="form__title">{titleOwner}</h3>
            )}
            {userType === 'pm' && (
                <h3 className="form__title">{titleManager}</h3>
            )}
            <p className="form__subtitle">{subtitle}</p>

            <form
                name="LYP-contact-lead-form"
                noValidate
                onSubmit={handleSubmit(onSubmit)}
            >
                <div className="form-combined">
                    <div className="form-combined-row">
                        <Controller
                            name="comment"
                            control={control}
                            render={({value, onChange}) => {
                                return (
                                    <FormTextArea
                                        id="comment"
                                        label={labels.comment}
                                        value={value}
                                        onChange={onChange}
                                    />
                                );
                            }}
                        />
                    </div>

                    <div className="form-combined-row">
                        <Controller {...getFormInputProps('firstName')} />
                        <Controller {...getFormInputProps('lastName')} />
                    </div>

                    <div className="form-combined-row">
                        <PhoneInput
                            name="phoneNumber"
                            control={control}
                            label={labels.phone}
                            countryCodes={countryCodes}
                            defaultCountryCode={countryCode}
                            error={errors.phoneNumber}
                            errorMsg={'Required'}
                        />
                    </div>

                    <div className="form-combined-row">
                        <Controller {...getFormInputProps('email')} />
                    </div>

                    <div className="form-combined-row">
                        <Controller
                            name="langPref"
                            control={control}
                            render={({value, onChange}) => {
                                return (
                                    <FormSelect
                                        name="langPref"
                                        label={labels.langPref}
                                        items={languageItems}
                                        value={value}
                                        onChange={(event) =>
                                            handlePreferredLanguageSelect(
                                                event,
                                                onChange,
                                            )
                                        }
                                    />
                                );
                            }}
                        />
                    </div>

                    {otherPreferredLanguage && (
                        <div className="form-combined-row">
                            <Controller
                                name="langPrefOther"
                                control={control}
                                render={({value, onChange}) => {
                                    return (
                                        <FormTextArea
                                            id="langPrefOther"
                                            label={pleaseSpecify}
                                            value={value}
                                            onChange={onChange}
                                        />
                                    );
                                }}
                            />
                        </div>
                    )}

                    {/* only for property managers */}
                    {userType === 'pm' && (
                        <div className="form-combined-row">
                            <Controller {...getFormInputProps('numProps')} />
                        </div>
                    )}

                    {/* only for property managers */}
                    {userType === 'pm' && (
                        <div className="form-combined-row">
                            <Controller
                                {...getFormInputProps('pmSoftware', false)}
                            />
                        </div>
                    )}
                </div>

                <div className="button-container">
                    <input
                        className="btn btn-primary btn-submit"
                        type="submit"
                        value={labels.submit}
                        disabled={formState.isSubmitting}
                    />
                </div>

                {submitError && (
                    <div className="error-message">
                        <p
                            dangerouslySetInnerHTML={{
                                __html: submissionErrorMsg,
                            }}
                        />
                    </div>
                )}

                <p
                    className="form__legal"
                    dangerouslySetInnerHTML={{__html: legal}}
                />
                {labels.optOut.length > 0 && (
                    <Checkbox
                        size="sm"
                        className="small"
                        name="optOut"
                        onChange={(event) => setOptOut(event.target.checked)}
                        checked={isOptOut}
                        label={labels.optOut}
                    />
                )}
            </form>

            <div className="contact__back-link">
                <ButtonLink label={backText} onClick={onBackClick} />
            </div>
        </div>
    );
};

export default Form;
